import React from "react";

/**
 * Lightweight inline SVG icon set — replaces the Font Awesome 4 webfont.
 * Stroke icons share a 24x24 box, 1.5 stroke, round caps/joins so they read
 * as one family. Brand icons are filled paths and opt out via `fill`.
 */
const strokeIcons = {
  monitor: (
    <>
      <rect x="2.5" y="3.5" width="19" height="13" rx="2" />
      <path d="M8 20.5h8M12 16.5v4" />
    </>
  ),
  smartphone: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </>
  ),
  pen: (
    <>
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7.5 18.5l-4 1 1-4Z" />
      <path d="M14 6l4 4" />
    </>
  ),
  layers: (
    <>
      <path d="M12 2.5 21.5 7 12 11.5 2.5 7 12 2.5Z" />
      <path d="m2.5 12 9.5 4.5 9.5-4.5" />
      <path d="m2.5 17 9.5 4.5 9.5-4.5" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v16.5a1.5 1.5 0 0 0 1.5 1.5H21" />
      <path d="M7.5 15.5V11M12 15.5V7M16.5 15.5v-3" />
    </>
  ),
  cloud: (
    <>
      <path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97 6 6 0 0 0-11.62 1.6A3.75 3.75 0 0 0 7 19Z" />
    </>
  ),
  shield: (
    <>
      <path d="M12 2.5 4.5 5.5v6c0 4.6 3.1 8.4 7.5 10 4.4-1.6 7.5-5.4 7.5-10v-6L12 2.5Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  zap: <path d="M13 2.5 4.5 13.5H11l-1 8 8.5-11H12l1-8Z" />,
  users: (
    <>
      <path d="M16 20.5v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 20.5v-2a4 4 0 0 0-3-3.87M16.5 3.6a4 4 0 0 1 0 7.75" />
    </>
  ),
  refresh: (
    <>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3.5V10h-6.5" />
    </>
  ),
  message: (
    <path d="M21 11.5a8.38 8.38 0 0 1-9 8.5 9.5 9.5 0 0 1-4-.9L3 21l1.9-4.5A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.5 8.5 0 0 1 21 11.5Z" />
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="m15.8 8.2-2.1 5.6-5.5 2.1 2.1-5.6Z" />
    </>
  ),
  rocket: (
    <>
      <path d="M12 2.5s4.5 2 4.5 8c0 3-1.5 5.5-1.5 5.5h-6S7.5 13.5 7.5 10.5c0-6 4.5-8 4.5-8Z" />
      <path d="M9 16s-3 .5-3 3.5c0 0 2.5.5 3.5-1M15 16s3 .5 3 3.5c0 0-2.5.5-3.5-1" />
      <circle cx="12" cy="9.5" r="1.6" />
    </>
  ),
  headset: (
    <>
      <path d="M4 14v-2a8 8 0 1 1 16 0v2" />
      <path d="M20 15.5a2.5 2.5 0 0 1-2.5 2.5H17v-6h.5A2.5 2.5 0 0 1 20 14.5ZM4 15.5A2.5 2.5 0 0 0 6.5 18H7v-6h-.5A2.5 2.5 0 0 0 4 14.5Z" />
      <path d="M20 17v.5a3.5 3.5 0 0 1-3.5 3.5H13" />
    </>
  ),
  check: <path d="m4.5 12.5 5 5 10-11" />,
  arrowRight: (
    <>
      <path d="M4 12h15.5" />
      <path d="m13.5 5.5 6.5 6.5-6.5 6.5" />
    </>
  ),
  arrowUpRight: (
    <>
      <path d="M7 17 17 7" />
      <path d="M8.5 7H17v8.5" />
    </>
  ),
  mapPin: (
    <>
      <path d="M20 10.5c0 5.5-8 12-8 12s-8-6.5-8-12a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10.5" r="3" />
    </>
  ),
  phone: (
    <path d="M21.5 16.9v2.5a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 1.6 3.7 2 2 0 0 1 3.6 1.5h2.5a2 2 0 0 1 2 1.7c.13.98.36 1.93.7 2.84a2 2 0 0 1-.45 2.11L7.3 9.2a16 16 0 0 0 6 6l1.05-1.05a2 2 0 0 1 2.11-.45c.9.34 1.86.57 2.84.7a2 2 0 0 1 1.7 2.03Z" />
  ),
  mail: (
    <>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="m3.5 7 8.5 5.5L20.5 7" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 6.5V12l3.5 2" />
    </>
  ),
  menu: <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  star: (
    <path d="m12 3 2.7 5.6 6.1.85-4.4 4.3 1.05 6.1L12 17l-5.45 2.85 1.05-6.1-4.4-4.3 6.1-.85Z" />
  ),
  quote: (
    <path d="M9.5 5.5C6.5 7 4.5 9.8 4.5 13v5.5h6V13h-3c0-2.2 1-3.9 3-5Zm10 0c-3 1.5-5 4.3-5 7.5v5.5h6V13h-3c0-2.2 1-3.9 3-5Z" />
  ),
};

const brandIcons = {
  linkedin:
    "M6.94 5.5a1.94 1.94 0 1 1-3.88 0 1.94 1.94 0 0 1 3.88 0ZM3.2 21h3.5V8.9H3.2V21Zm6.06-12.1V21h3.5v-6.35c0-1.72.66-2.6 1.94-2.6 1.2 0 1.8.85 1.8 2.6V21h3.5v-6.9c0-3.4-1.86-5.04-4.3-5.04-1.68 0-2.65.85-3.03 1.62V8.9h-3.4Z",
  x: "M17.53 3h3.2l-7 8 8.23 10h-6.45l-5.05-6.1L4.7 21H1.5l7.5-8.57L1.1 3h6.6l4.56 5.6L17.53 3Zm-1.12 16.1h1.77L7.7 4.8H5.8l10.6 14.3Z",
  facebook:
    "M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.5-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z",
  youtube:
    "M22.54 6.42a2.79 2.79 0 0 0-1.96-1.98C18.8 4 12 4 12 4s-6.8 0-8.58.44a2.79 2.79 0 0 0-1.96 1.98A29.3 29.3 0 0 0 1 12a29.3 29.3 0 0 0 .46 5.58 2.79 2.79 0 0 0 1.96 1.98C5.2 20 12 20 12 20s6.8 0 8.58-.44a2.79 2.79 0 0 0 1.96-1.98A29.3 29.3 0 0 0 23 12a29.3 29.3 0 0 0-.46-5.58ZM9.75 15.27V8.73L15.5 12l-5.75 3.27Z",
  github:
    "M12 2C6.48 2 2 6.58 2 12.25c0 4.53 2.87 8.37 6.84 9.73.5.09.68-.22.68-.5v-1.7c-2.78.62-3.37-1.37-3.37-1.37-.45-1.19-1.11-1.5-1.11-1.5-.91-.64.07-.63.07-.63 1 .07 1.53 1.06 1.53 1.06.9 1.57 2.35 1.12 2.92.86.09-.67.35-1.12.63-1.38-2.22-.26-4.55-1.14-4.55-5.05 0-1.12.39-2.03 1.02-2.74-.1-.26-.44-1.3.1-2.7 0 0 .84-.28 2.75 1.05a9.28 9.28 0 0 1 5 0c1.91-1.33 2.75-1.05 2.75-1.05.54 1.4.2 2.44.1 2.7.63.71 1.02 1.62 1.02 2.74 0 3.92-2.34 4.79-4.57 5.04.36.32.68.94.68 1.9v2.82c0 .28.18.6.69.5A10.26 10.26 0 0 0 22 12.25C22 6.58 17.52 2 12 2Z",
};

export const Icon = ({ name, className = "h-6 w-6", ...rest }) => {
  const stroke = strokeIcons[name];

  if (stroke) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        {stroke}
      </svg>
    );
  }

  const brand = brandIcons[name];
  if (!brand) return null;

  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={brand} />
    </svg>
  );
};

export default Icon;
